import environment from '@department-of-veterans-affairs/platform-utilities/environment';
import { datadogRum } from '@datadog/browser-rum';
import { dataDogLogger } from 'platform/monitoring/Datadog';
import { APP_NAMES } from '../appConstants';

/**
 * Build a GA event name: `${eventType}-${appPrefix}${slug}`.
 * Pass `app` from every component that more than one app renders; without
 * it the slug carries the day-of `check-in-` prefix regardless of the app.
 * @param {string} slug
 * @param {string} [eventType]
 * @param {string} [app] one of APP_NAMES
 */
const createAnalyticsSlug = (slug, eventType = '', app = '') => {
  let prefix = '';
  if (eventType) {
    prefix = `${eventType}-`;
  }
  let context = 'check-in-';
  switch (app) {
    case APP_NAMES.PRE_CHECK_IN:
      context = 'pre-check-in-';
      break;
    case APP_NAMES.TRAVEL_CLAIM:
      context = 'travel-claim-';
      break;
    default:
      break;
  }

  return `${prefix}${context}${slug}`;
};

/**
 * @typedef {Object} CreateApiEventResponse
 * @property {string} event
 * @property {string} api-name
 * @property {string} api-status
 * @property {number} [api_latency_ms]
 * @property {string} [error-key]
 */

/**
 * @param {string} [name]
 * @param {string} [status]
 * @param {number} [time]
 * @param {string} [error]
 * @returns {CreateApiEventResponse}
 */
const createApiEvent = (name, status, time, error) => {
  const rv = {
    event: 'api_call',
    'api-name': name,
    'api-status': status,
  };
  if (time) {
    // eslint-disable-next-line camelcase
    rv.api_latency_ms = time;
  }
  if (error) {
    rv['error-key'] = error;
  }
  return rv;
};

const ERROR_SOURCES = Object.freeze({
  API: 'api',
  OTHER: 'other',
});

/**
 * Report a client error to Datadog Browser Logs and RUM.
 *
 * Logs the error message, the api event name and the session token, and
 * nothing else. API response bodies hold demographics and contact data and
 * must never be passed here; vets-api already logs upstream error bodies
 * scrubbed, and the two join on the UUID.
 *
 * @param {Error|{source: string, err: *}} error
 * @param {{token?: string, eventName?: string}} [details]
 */
const captureError = (error, details = {}) => {
  const { token, eventName } = details;
  let message;
  if (error?.source === ERROR_SOURCES.API) {
    message = `check_in_client_api_error-${eventName}`;
  } else if (error instanceof Error) {
    message = error.message;
  } else {
    message = 'check_in_client_error';
  }
  const errorInstance = error instanceof Error ? error : new Error(message);
  const context = { eventName, token };

  dataDogLogger({
    message,
    attributes: context,
    status: 'error',
    error: errorInstance,
  });
  datadogRum.addError(errorInstance, context);

  if (!environment.isProduction()) {
    // eslint-disable-next-line no-console
    console.error(error);
  }
};

export { createAnalyticsSlug, createApiEvent, captureError, ERROR_SOURCES };
